import axios from "axios";
import { useEffect, useState } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import { API_URL } from "../../url/api";

type UserProfileProps = {
  name: string;
  address: string;
  email: string;
  phonenumber: string;
  emailAlerts: boolean;
};

const UserProfile = () => {
  const [originalUserData, setOriginalUserData] = useState<UserProfileProps>({
    name: "",
    address: "",
    email: "",
    phonenumber: "",
    emailAlerts: true,
  });
  const [userData, setUserData] = useState<UserProfileProps>({
    name: "",
    address: "",
    email: "",
    phonenumber: "",
    emailAlerts: true,
  });

  const [loading, setLoading] = useState<boolean>(false);

  async function fetchUserData() {
    try {
      setLoading(true);
      const userId = localStorage.getItem("userId");
      const response = await axios.get(`${API_URL}user/${userId}/`);
      console.log("🚀 ~ fetchUserData ~ response:", response);
      if (response.data) {
        setUserData(response.data);
        setOriginalUserData(response.data);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching user data: ", error);
    }
  }

  async function updateUserData() {
    try {
      setLoading(true);
      const userId = localStorage.getItem("userId");
      const response = await axios.put(`${API_URL}user/${userId}/`, userData);
      console.log("🚀 ~ updateUserData ~ response:", response);
      if (response.data) {
        setUserData(response.data);
        setOriginalUserData(response.data);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error updating user data: ", error);
    }
  }

  async function deleteUserData() {
    try {
      setLoading(true);
      const userId = localStorage.getItem("userId");
      const response = await axios.get(
        `${API_URL}delete/user-account/${userId}/`
      );
      console.log("🚀 ~ deleteUserData ~ response:", response);
      if (response.data && response.data.success) {
        localStorage.removeItem("userId");
        window.location.href = "/";
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error deleting user data: ", error);
    }
  }

  useEffect(() => {
    // Fetch user data here
    fetchUserData();

    return () => {};
  }, []);

  const [editMode, setEditMode] = useState(false);

  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleDeleteAccount = () => {
    console.log("Account deleted");
    setShowConfirmation(false);
    deleteUserData();
  };

  const handleEditMode = () => {
    setEditMode(!editMode);
  };

  const handleSaveChanges = () => {
    if (isDataChanged()) {
      updateUserData();
    }
    setEditMode(false);
  };

  const isDataChanged = () => {
    return (
      originalUserData.name !== userData.name ||
      originalUserData.address !== userData.address ||
      originalUserData.email !== userData.email ||
      originalUserData.phonenumber !== userData.phonenumber ||
      originalUserData.emailAlerts !== userData.emailAlerts
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="max-w-6xl m-6 px-4 md:w-3/4 h-3/4">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-3xl font-semibold mb-6">User Profile</h1>
        {!editMode && (
          <FaEdit
            className="text-gray-500 text-2xl mb-4 cursor-pointer"
            color="#000"
            onClick={handleEditMode}
          />
        )}
      </div>

      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Personal Details</h2>
          <hr className="mb-4" />
          <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mt-2 md:mt-4">
              <p className="text-gray-600">Name</p>
              {editMode ? (
                <input
                  type="text"
                  className="border border-gray-300 rounded w-full p-2"
                  value={userData.name}
                  onChange={(e) =>
                    setUserData({ ...userData, name: e.target.value })
                  }
                />
              ) : (
                <p>{userData.name}</p>
              )}
            </div>
            <div className="mt-2 md:mt-4">
              <p className="text-gray-600">Address</p>
              {editMode ? (
                <textarea
                  className="border border-gray-300 rounded w-full p-2"
                  value={userData.address}
                  onChange={(e) =>
                    setUserData({ ...userData, address: e.target.value })
                  }
                />
              ) : (
                <p>{userData.address}</p>
              )}
            </div>
            <div className="mt-2 md:mt-4">
              <p className="text-gray-600">Email</p>
              {editMode ? (
                <input
                  type="email"
                  className="border border-gray-300 rounded w-full p-2"
                  disabled
                  value={userData.email}
                  onChange={(e) =>
                    setUserData({ ...userData, email: e.target.value })
                  }
                />
              ) : (
                <p>{userData.email}</p>
              )}
            </div>
            <div className="mt-2 md:mt-4">
              <p className="text-gray-600">Phone Number</p>
              {editMode ? (
                <input
                  type="text"
                  className="border border-gray-300 rounded w-full p-2"
                  value={userData.phonenumber}
                  onChange={(e) =>
                    setUserData({ ...userData, phonenumber: e.target.value })
                  }
                />
              ) : (
                <p>{userData.phonenumber}</p>
              )}
            </div>
            <div className="mt-2 md:mt-4">
              <p className="text-gray-600">Email Alerts</p>
              <p>{userData.emailAlerts ? "Enabled" : "Disabled"}</p>
            </div>
          </div>
        </div>

        {!editMode ? (
          <div className="flex justify-end mt-2 md:mt-4">
            <button
              className="bg-red-500 text-white px-4 py-2 rounded w-full md:w-auto "
              onClick={() => setShowConfirmation(true)}
            >
              <FaTrash className="inline-block mr-2" />
              Delete Account
            </button>
          </div>
        ) : (
          <div className="flex justify-end items-center gap-5 flex-col-reverse md:flex-row">
            <div className="flex justify-end mt-2 md:mt-4">
              <button
                className="border border-primary text-primary px-4 py-2 rounded w-full md:w-auto "
                onClick={handleEditMode}
              >
                {/* <FaTrash className="inline-block mr-2" /> */}
                Cancel
              </button>
            </div>
            {isDataChanged() && (
              <div className="flex justify-end mt-2 md:mt-4">
                <button
                  className="bg-primary text-white px-4 py-2 rounded w-full md:w-auto "
                  onClick={handleSaveChanges}
                >
                  {/* <FaTrash className="inline-block mr-2" /> */}
                  Save Changes
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showConfirmation && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4">
              Confirm Account Deletion
            </h3>
            <p className="mb-6">
              Are you sure you want to delete your account?
            </p>
            <div className="flex justify-end">
              <button
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded mr-4"
                onClick={() => setShowConfirmation(false)}
              >
                Cancel
              </button>
              <button
                className="bg-red-500 text-white px-4 py-2 rounded"
                onClick={handleDeleteAccount}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
